import { Container, Graphics } from 'pixi.js';
import { type DesertPalette, desertAt } from '../../shared/full/palette';
import type { Vec2 } from '../../shared/vec';
import type { Frame } from '../frame';
import { type Layout, OVERFLOW } from '../ground-layout';
import { mixColor, parseHexColor } from '../palette';
import {
  type Rug,
  type Weave,
  WEAVES,
  diamondsOf,
  dunesOf,
  khatam,
  ripplesOf,
  rugPoint,
  rugsOf,
} from './desert-layout';

const TAU = Math.PI * 2;
const WHITE = 0xffffff;
const RIPPLE_ALPHA = 0.75;
const GLINT_ALPHA = 0.35;
const CREST_ALPHA = 0.8;
const LEE_ALPHA = 0.28;
const LINE_ALPHA = 0.65;
const ROSETTE_ALPHA = 0.75;
const MOTIF_SHARE = 0.55;
const HEART_SHARE = 0.45;
const FRAME_INSET = 6;
const FRINGE = 7;
const ROSETTE = { star: 124, inner: 104, ring: 86, dots: 146 } as const;

// Rugs are solid, mixed into the sand by their strength: their layers never show through.
export function desertTones(desert: DesertPalette) {
  const sand = parseHexColor(desert.sable);
  const rug = (color: number) => mixColor(sand, color, desert.tapis);
  const tone = (key: keyof Omit<DesertPalette, 'tapis'>) => parseHexColor(desert[key]);
  return {
    sand,
    ripple: mixColor(sand, tone('ride'), RIPPLE_ALPHA),
    crest: mixColor(sand, tone('crete'), CREST_ALPHA),
    lee: mixColor(sand, tone('abri'), LEE_ALPHA),
    rosette: mixColor(sand, tone('rosace'), ROSETTE_ALPHA),
    weave: ({ field, motif, border }: Weave) => ({
      field: rug(tone(field)),
      motif: rug(mixColor(tone(field), tone(motif), MOTIF_SHARE)),
      border: rug(tone(border)),
    }),
  };
}

function trace(graphics: Graphics, points: readonly Vec2[]): void {
  points.forEach(({ x, y }, index) => {
    if (index === 0) {
      graphics.moveTo(x, y);
    } else {
      graphics.lineTo(x, y);
    }
  });
}

function rect(rug: Rug, inset: number): Vec2[] {
  const across = rug.width / 2 - inset;
  const along = rug.length / 2 - inset;
  return [
    rugPoint(rug, -across, -along),
    rugPoint(rug, across, -along),
    rugPoint(rug, across, along),
    rugPoint(rug, -across, along),
  ];
}

function dashes(graphics: Graphics, from: Vec2, to: Vec2, on: number, off: number): void {
  const length = Math.hypot(to.x - from.x, to.y - from.y);
  for (let start = 0; start < length; start += on + off) {
    const end = Math.min(start + on, length);
    graphics
      .moveTo(
        from.x + ((to.x - from.x) * start) / length,
        from.y + ((to.y - from.y) * start) / length,
      )
      .lineTo(from.x + ((to.x - from.x) * end) / length, from.y + ((to.y - from.y) * end) / length);
  }
}

export function createDesert(parent: Container) {
  const root = new Container();
  const surface = new Graphics();
  const lee = new Graphics();
  const crests = new Graphics();
  const ripples = new Graphics();
  const glints = new Graphics();
  const weaves = new Map(
    WEAVES.map((weave) => [
      weave,
      {
        field: new Graphics(),
        frame: new Graphics(),
        motif: new Graphics(),
        heart: new Graphics(),
      },
    ]),
  );
  const lines = new Graphics();
  const rosette = new Graphics();
  root.addChild(surface, lee, crests, ripples, glints);
  for (const role of ['field', 'frame', 'motif', 'heart'] as const) {
    root.addChild(...[...weaves.values()].map((weave) => weave[role]));
  }
  root.addChild(lines, rosette);
  parent.addChild(root);

  return {
    root,
    draw(layout: Layout, seed: number): void {
      const { arena } = layout;
      const center = { x: arena.width / 2, y: arena.height / 2 };

      surface
        .clear()
        .rect(-OVERFLOW, -OVERFLOW, arena.width + 2 * OVERFLOW, arena.height + 2 * OVERFLOW)
        .fill(WHITE);
      lee.clear();
      crests.clear();
      for (const crest of dunesOf(arena)) {
        trace(lee, crest);
        trace(crests, crest);
      }
      lee.stroke({ width: 70, color: WHITE, cap: 'round', join: 'round' });
      crests.stroke({ width: 5, color: WHITE, cap: 'round', join: 'round' });
      ripples.clear();
      glints.clear();
      for (const ripple of ripplesOf(arena, seed)) {
        trace(ripples, ripple);
        trace(
          glints,
          ripple.map(({ x, y }) => ({ x, y: y - 3 })),
        );
      }
      ripples.stroke({ width: 2.4, color: WHITE });
      glints.stroke({ width: 1.2, color: WHITE });

      for (const weave of weaves.values()) {
        for (const graphics of Object.values(weave)) {
          graphics.clear();
        }
      }
      lines.clear();
      for (const rug of rugsOf(center, seed)) {
        const weave = weaves.get(rug.weave);
        if (weave === undefined) {
          throw new Error('A rug of a weave the desert does not know');
        }
        const across = rug.width / 2;
        const along = rug.length / 2;
        for (let fringe = -across + 4; fringe <= across - 4; fringe += 6) {
          for (const end of [-1, 1]) {
            trace(lines, [
              rugPoint(rug, fringe, end * along),
              rugPoint(rug, fringe, end * (along + FRINGE)),
            ]);
          }
        }
        weave.field.poly(rect(rug, 0)).fill(WHITE);
        const frame = rect(rug, FRAME_INSET);
        weave.frame.poly(frame).stroke({ width: 5, color: WHITE });
        frame.forEach((corner, index) => {
          dashes(lines, corner, frame[(index + 1) % frame.length] ?? corner, 4, 4);
        });
        for (const { along: middle, halfWidth, halfLength } of diamondsOf(rug)) {
          const diamond = (share: number) => [
            rugPoint(rug, 0, middle - halfLength * share),
            rugPoint(rug, halfWidth * share, middle),
            rugPoint(rug, 0, middle + halfLength * share),
            rugPoint(rug, -halfWidth * share, middle),
          ];
          weave.motif.poly(diamond(1)).fill(WHITE);
          lines.poly(diamond(1));
          weave.heart.poly(diamond(HEART_SHARE)).fill(WHITE);
        }
      }
      lines.stroke({ width: 1.3, color: WHITE });

      rosette
        .clear()
        .poly(khatam(center, ROSETTE.star))
        .stroke({ width: 2.5, color: WHITE })
        .poly(khatam(center, ROSETTE.inner, Math.PI / 8))
        .circle(center.x, center.y, ROSETTE.ring)
        .stroke({ width: 1.2, color: WHITE });
      const { dots } = ROSETTE;
      for (let angle = 0; angle < TAU; angle += 8 / dots) {
        rosette
          .moveTo(center.x + dots * Math.cos(angle), center.y + dots * Math.sin(angle))
          .arc(center.x, center.y, dots, angle, angle + 2 / dots);
      }
      rosette.stroke({ width: 2.5, color: WHITE });
    },
    style(frame: Frame): void {
      const desert = desertAt(frame.fraction);
      const tones = desertTones(desert);
      surface.tint = tones.sand;
      lee.tint = parseHexColor(desert.abri);
      lee.alpha = LEE_ALPHA;
      crests.tint = parseHexColor(desert.crete);
      crests.alpha = CREST_ALPHA;
      ripples.tint = parseHexColor(desert.ride);
      ripples.alpha = RIPPLE_ALPHA;
      glints.tint = parseHexColor(desert.crete);
      glints.alpha = GLINT_ALPHA;
      for (const [weave, layers] of weaves) {
        const tone = tones.weave(weave);
        layers.field.tint = tone.field;
        layers.frame.tint = tone.border;
        layers.motif.tint = tone.motif;
        layers.heart.tint = tone.border;
      }
      lines.tint = parseHexColor(desert.trait);
      lines.alpha = LINE_ALPHA * desert.tapis;
      rosette.tint = parseHexColor(desert.rosace);
      rosette.alpha = ROSETTE_ALPHA;
    },
    destroy: () => undefined,
  };
}
